import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  auth,
  onAuthStateChanged,
  signInWithGoogle as fbSignIn,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from '../lib/firebase';
import { syncUser, ApiError } from '../lib/api';
import type { AdminUser } from '../lib/types';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  adminUser: AdminUser | null;
  isAdmin: boolean | null; // null while loading
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async (fu: FirebaseUser | null) => {
    if (!fu) {
      setAdminUser(null);
      setLoading(false);
      return;
    }
    try {
      // Idempotent find-or-create. On first Google sign-in this creates
      // the DB row that gates /admin/* downstream; on subsequent loads it
      // just returns the existing row.
      const me = await syncUser({
        firebase_uid: fu.uid,
        email: fu.email,
        display_name: fu.displayName,
      });
      setAdminUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Token issue; force re-auth by signing out.
        await fbSignOut();
        setAdminUser(null);
      } else {
        // 5xx, network failure, or unexpected — leave adminUser null so
        // the gate denies access and the UI shows the "Not authorized"
        // panel with a sign-out link. (The 404-on-/users/me case is no
        // longer reachable here because /users/sync find-or-creates.)
        setAdminUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fu) => {
      setFirebaseUser(fu);
      setLoading(true);
      void bootstrap(fu);
    });
    return () => unsub();
  }, [bootstrap]);

  const signIn = useCallback(async () => {
    await fbSignIn();
    // onAuthStateChanged fires; bootstrap runs there.
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await bootstrap(auth.currentUser);
  }, [bootstrap]);

  const isAdmin = loading ? null : adminUser?.is_admin === true;

  const value = useMemo<AuthState>(
    () => ({ firebaseUser, adminUser, isAdmin, loading, signIn, signOut, refresh }),
    [firebaseUser, adminUser, isAdmin, loading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
